import { apiErrorMessage } from '@/api/client';
import { promptsApi } from '@/api/prompts.api';
import { authorKind } from '@/components/admin/ui';
import { ErrorMessage } from '@/components/common/ErrorMessage';
import { ConfirmModal } from '@/components/common/Modal';
import type { PromptKind, SystemPromptModel } from '@/types/categories.types';
import type { FC } from 'react';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

interface SystemPromptViewProps {
  /** The caller's username, for "· vy". */
  me: string;
}

interface EditorProps extends SystemPromptViewProps {
  kind: PromptKind;
}

const ghost = (enabled: boolean): string =>
  `rounded border border-control bg-white px-3.5 py-[9px] text-[12.5px] font-semibold ${
    enabled ? 'cursor-pointer text-muted hover:border-primary hover:text-primary' : 'cursor-default text-dash'
  }`;

/** `2026-09-23T08:05:00Z` → `23. 9. 2026 10:05` in the viewer's time zone. */
const formatStamp = (iso: string): string => {
  const date = new Date(iso);
  return `${date.toLocaleDateString('cs-CZ')} ${date.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })}`;
};

/**
 * "Systémový prompt" (superadmin only): the two LLM prompts — item detection (`system`) and the document's
 * own sensitivity declarations (`declaration`) — as two editors under each other, each loaded and saved on
 * its own.
 */
export const SystemPromptView: FC<SystemPromptViewProps> = ({ me }) => {
  const { t } = useTranslation();

  return (
    <>
      <div className="flex items-center gap-2">
        <h1 className="m-0 text-xl font-bold text-primary">{t('admin.views.system')}</h1>
        <span className="bg-[#fbf1de] px-[7px] py-[3px] text-[10.5px] font-semibold tracking-[.04em] text-[#7a4d00]">
          {t('admin.system.badge')}
        </span>
      </div>
      <Editor kind="system" me={me} />
      <Editor kind="declaration" me={me} />
    </>
  );
};

/**
 * Exactly the stored template of one prompt, placeholders included, in a monospace editor with
 * "Obnovit výchozí" (the shipped default into the editor, after a confirm), "Zahodit změny" and
 * "Uložit prompt". Nothing is validated beyond non-empty — the text is the superadmin's responsibility.
 */
const Editor: FC<EditorProps> = ({ kind, me }) => {
  const { t, i18n } = useTranslation();
  const headingId = useId();
  const [saved, setSaved] = useState<SystemPromptModel | null>(null);
  const [draft, setDraft] = useState('');
  const [loadError, setLoadError] = useState<unknown>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  // `kind` only: `t` changes identity on a language switch, which must not reload and overwrite the
  // draft — the raw error is kept and translated at render time.
  useEffect(() => {
    setLoadError(null);
    promptsApi
      .getPrompt(kind)
      .then((prompt) => {
        setSaved(prompt);
        setDraft(prompt.text);
      })
      .catch(setLoadError);
  }, [kind]);

  const dirty = saved !== null && draft !== saved.text;
  const isDefaultText = saved !== null && draft === saved.default_text;
  const canSave = dirty && draft.trim().length > 0 && !isSaving;

  const meta = !saved
    ? ''
    : saved.author === 'default'
      ? t('admin.system.default')
      : t('admin.system.edited', {
          date: formatStamp(saved.updated_at),
          name: authorKind(saved.author, me) === 'me' ? t('admin.who.me') : saved.author,
        });

  const save = async () => {
    if (!canSave) return;
    setIsSaving(true);
    try {
      const next = await promptsApi.updatePrompt(kind, draft);
      setSaved(next);
      setDraft(next.text);
    } catch (err) {
      toast.error(apiErrorMessage(err, t('admin.saveFailed')));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section aria-labelledby={headingId} className="mt-[18px] border-t border-line pt-[18px]">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0 flex-[1_1_320px]">
          <h2 id={headingId} className="m-0 mb-[5px] text-base font-bold text-primary">
            {t(`admin.${kind}.title`)}
          </h2>
          <p className="m-0 max-w-[680px] text-[12.5px] leading-[1.55] text-muted">{t(`admin.${kind}.intro`)}</p>
        </div>
        <span className="text-[11.5px] text-label-2">{meta}</span>
      </div>
      <div className="mt-[18px] flex flex-col gap-2.5">
        {loadError !== null ? (
          <ErrorMessage message={apiErrorMessage(loadError, t('admin.loadFailed'))} />
        ) : (
          <>
            <textarea
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              spellCheck={false}
              disabled={saved === null || isSaving}
              aria-labelledby={headingId}
              maxLength={20000}
              className="min-h-[420px] w-full resize-y rounded border border-control bg-white px-4 py-3.5 font-mono text-[12.5px] leading-[1.65] text-ink focus:border-primary focus:outline-none"
            />
            <div className="flex flex-wrap items-center gap-2.5">
              <span className="flex-1 text-[11.5px] text-label-2">
                {dirty
                  ? t('admin.system.dirty')
                  : t('admin.system.chars', { n: draft.length.toLocaleString(i18n.language) })}
              </span>
              <button
                type="button"
                aria-describedby={headingId}
                disabled={saved === null || isDefaultText}
                onClick={() => setConfirmReset(true)}
                className={ghost(saved !== null && !isDefaultText)}
              >
                {t('admin.system.reset')}
              </button>
              <button
                type="button"
                aria-describedby={headingId}
                disabled={!dirty}
                onClick={() => saved && setDraft(saved.text)}
                className={ghost(dirty)}
              >
                {t('admin.system.revert')}
              </button>
              <button
                type="button"
                aria-describedby={headingId}
                disabled={!canSave}
                onClick={save}
                className={`rounded border-none px-4 py-[9px] text-[12.5px] font-semibold ${
                  canSave ? 'cursor-pointer bg-primary text-white' : 'cursor-not-allowed bg-control text-faint'
                }`}
              >
                {t('admin.system.save')}
              </button>
            </div>
          </>
        )}
      </div>

      {confirmReset && saved ? (
        <ConfirmModal
          title={t('admin.system.resetTitle')}
          text={t(`admin.${kind}.resetText`)}
          confirmLabel={t('admin.system.resetOk')}
          cancelLabel={t('admin.system.cancel')}
          onConfirm={() => {
            setDraft(saved.default_text);
            setConfirmReset(false);
          }}
          onCancel={() => setConfirmReset(false)}
        />
      ) : null}
    </section>
  );
};
